import React, { useCallback } from "react";
import { Text } from "web/react/emo/text";
import { useShopfrontSectionImpression } from "web/react/pages/feed/shopfront-feed/use-shopfront-section-impression";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { MediaSlotSerializer } from "web/types/serializers";
import * as styles from "./shopfront-media-slot.css";

export interface ShopfrontMediaSlotProps {
    slot: MediaSlotSerializer;
    ctaLabel?: string;
    // CW-3710: set to fire retailer_media_slot_1/2 viewed+clicked events; left
    // undefined for the hero (media_slot_1), which isn't tracked by this ticket.
    analyticsCategory?: string;
}

export function ShopfrontMediaSlot({
    slot,
    ctaLabel,
    analyticsCategory,
}: ShopfrontMediaSlotProps): React.ReactElement | null {
    const sectionRef = useShopfrontSectionImpression<HTMLElement>(analyticsCategory);

    const onClick = useCallback(() => {
        if (analyticsCategory) {
            analytics.event({ category: analyticsCategory, action: "clicked" });
        }
    }, [analyticsCategory]);

    if (!slot.image_url) {
        return null;
    }

    const image = <img src={slot.image_url} alt="" className={styles.image} />;

    // An unlinked slot still reports its "viewed" event, so tracking doesn't depend
    // on whether a partner configured a link for the slot.
    if (!slot.link_url) {
        return (
            <div className={styles.container} ref={sectionRef as React.RefObject<HTMLDivElement>}>
                {image}
            </div>
        );
    }

    return (
        <a
            href={slot.link_url}
            className={styles.container}
            ref={sectionRef as React.RefObject<HTMLAnchorElement>}
            onClick={onClick}
        >
            {image}
            {slot.caption && (
                <span className={styles.captionContainer}>
                    <Text textStyle="body-xs-bold" as="span">
                        {slot.caption}
                    </Text>
                    <Text textStyle="body-xs" as="span" className={styles.exploreLink}>
                        {ctaLabel ?? gettext("shopfront.media_slot.explore")}
                    </Text>
                </span>
            )}
        </a>
    );
}
